import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { RotateCw } from 'lucide-react';
import type { OAuthAuthorizeDetailsDto, OAuthAuthorizeQuery } from '@aiai/shared';
import { decideOAuthAuthorize, getOAuthAuthorizeDetails } from '../api';
import { errorMessage } from '../lib/api';
import { Callout } from '../components/Callout';
import { Spinner } from '../components/Spinner';
import { LoadingState } from '../components/States';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

/** The authorization request, exactly as the API forwarded it to this page. */
function readQuery(search: string): OAuthAuthorizeQuery {
  return Object.fromEntries(new URLSearchParams(search));
}

function Brand() {
  return (
    <div className="flex items-center justify-center gap-2">
      <span className="grid h-7 w-7 place-items-center rounded-[5px] bg-ink text-surface-1">
        <RotateCw className="h-4 w-4" aria-hidden="true" />
      </span>
      <span className="font-display text-sm font-bold tracking-tight text-ink">
        aiai<span className="text-signal">·</span>loop
      </span>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <Brand />
        <div className="card space-y-4 p-6">{children}</div>
      </div>
    </div>
  );
}

/** Home-workspace default: the first workspace with MCP on. */
function defaultWorkspace(details: OAuthAuthorizeDetailsDto): string {
  return details.workspaces?.find((w) => w.mcpEnabled)?.id ?? '';
}

/**
 * The browser step of the MCP OAuth flow: an MCP client (Claude Code,
 * claude.ai, ChatGPT, Cursor…) sent the user here via `GET /api/oauth/authorize`.
 * Shows who is asking and what the connection may do, lets the user pick the
 * connection's home workspace, and hands the browser back to the client with a
 * code (approve) or `access_denied` (cancel). Full-screen, outside the app nav.
 */
export function OAuthConsentPage() {
  useDocumentTitle('Connect an app');
  const query = useMemo(() => readQuery(window.location.search), []);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const details = useQuery({
    queryKey: ['oauth-consent', query],
    queryFn: () => getOAuthAuthorizeDetails(query),
    retry: false,
    staleTime: Infinity,
  });
  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'deny') =>
      decideOAuthAuthorize({
        query,
        decision,
        workspaceId: decision === 'approve' ? (workspaceId ?? (details.data ? defaultWorkspace(details.data) : '')) : undefined,
      }),
    onSuccess: (res, decision) => {
      if (details.data?.nativeClient) {
        setDone(
          decision === 'approve'
            ? `Connected. You can close this tab and return to ${details.data.clientName}.`
            : 'Cancelled. You can close this tab.',
        );
      }
      window.location.assign(res.redirectTo);
    },
  });

  if (details.isLoading) {
    return (
      <Shell>
        <LoadingState label="Checking the request…" />
      </Shell>
    );
  }
  if (details.isError || !details.data) {
    return (
      <Shell>
        <h1 className="text-lg font-semibold text-ink">This connection request can’t be used</h1>
        <Callout tone="danger">{errorMessage(details.error, 'The request is invalid or has expired.')}</Callout>
        <p className="text-sm text-ink-muted">Start the connection again from your MCP client.</p>
      </Shell>
    );
  }

  const d = details.data;
  if (done) {
    return (
      <Shell>
        <h1 className="text-lg font-semibold text-ink">{d.clientName}</h1>
        <p className="text-sm text-ink-muted">{done}</p>
      </Shell>
    );
  }

  const workspaces = d.workspaces ?? [];
  const selected = workspaceId ?? defaultWorkspace(d);
  const canApprove = workspaces.some((w) => w.id === selected && w.mcpEnabled);

  return (
    <Shell>
      <div className="space-y-1">
        <h1 className="text-lg font-semibold text-ink">Connect {d.clientName}</h1>
        <p className="text-sm text-ink-muted">
          <span className="font-medium text-ink">{d.clientName}</span> wants to use aiai-loop as{' '}
          <span className="font-medium text-ink">{d.email}</span>.
        </p>
      </div>

      <div className="space-y-2 text-sm text-ink-muted">
        <p>It will be able to:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>read projects, runs (with logs and diffs), issues, loops and prototypes you can see</li>
          <li>file and update issues where you are allowed to</li>
          <li>redeploy prototypes you own</li>
        </ul>
        <p>It never sees secrets and can’t do more than you can in the app.</p>
      </div>

      {workspaces.length > 0 && (
        <label className="block space-y-1">
          <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">Home workspace</span>
          <select
            className="input"
            value={selected}
            onChange={(e) => setWorkspaceId(e.target.value)}
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id} disabled={!w.mcpEnabled}>
                {w.name}
                {w.mcpEnabled ? '' : ' (MCP switched off)'}
              </option>
            ))}
          </select>
          <span className="block text-xs text-ink-faint">
            Tools default to this workspace and can reach your other workspaces by name.
          </span>
        </label>
      )}
      {workspaces.length > 0 && !workspaces.some((w) => w.mcpEnabled) && (
        <Callout tone="warning">MCP is switched off for all of your workspaces.</Callout>
      )}

      <p className="text-xs text-ink-faint">
        You will be sent back to <span className="font-mono text-ink-muted">{d.redirectDisplay}</span>.
        Only continue if you started this from {d.clientName}.
      </p>

      {decide.isError && (
        <Callout tone="danger">{errorMessage(decide.error, 'Could not complete the connection')}</Callout>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="btn-secondary"
          disabled={decide.isPending}
          onClick={() => decide.mutate('deny')}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={decide.isPending || !canApprove}
          onClick={() => decide.mutate('approve')}
        >
          {decide.isPending && <Spinner className="h-3.5 w-3.5" />}
          Authorize
        </button>
      </div>
    </Shell>
  );
}
